import { useState, useEffect, useMemo, useCallback } from "react";
import AccountingRecordService from "../../../../services/accounting_record_service";
import FinancialCategoryService from "../../../../services/financial_category_service";
import { useTenant } from "../../../../hooks/useTenant";
import { buildDREData, filterByMonth } from "../utils/dreCalculations";

/**
 * useDRE
 * Hook que carrega os dados CONTÁBEIS Puros (accountingRecords + categorias)
 * e calcula o DRE para o mês/ano selecionado (Regime de Competência real).
 */
export const useDRE = () => {
    const { idTenant } = useTenant();

    // Filtros
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);

    // Dados
    const [allRecords, setAllRecords] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /**
     * Carrega todos os accountingRecords do ano selecionado e as categorias base.
     */
    const loadRecords = useCallback(async () => {
        if (!idTenant) return;

        setLoading(true);
        setError(null);

        try {
            // Carregar Plano de Contas raiz
            const cats = await FinancialCategoryService.getAll(idTenant);
            setCategories(cats);

            // Range do ano
            const startDate = `${year}-01-01`;
            const endDate = `${year}-12-31`;

            // NOVA FONTE DA VERDADE: ACCOUNTING_RECORDS
            const records = await AccountingRecordService.getByCompetenceDate(
                idTenant,
                startDate,
                endDate
            );

            // Filtrar apenas o registro completado (DRE lida com fatos geradores garantidos)
            const activeRecords = records.filter(r => r.status !== "CANCELED");

            setAllRecords(activeRecords);
        } catch (err) {
            console.error("[useDRE] Erro ao carregar dados do Livro Contábil:", err);
            setError(err.message || "Erro ao carregar dados do DRE.");
        } finally {
            setLoading(false);
        }
    }, [idTenant, year]);

    useEffect(() => {
        loadRecords();
    }, [loadRecords]);

    /**
     * DRE filtrado pelo mês selecionado.
     */
    const dreData = useMemo(() => {
        const emptyTotals = {
            totalIncome: 0, totalExpense: 0, netResult: 0, margin: 0,
        };
        if (!allRecords.length && !categories.length) {
            return { income: [], expense: [], totals: emptyTotals };
        }

        const filtered = filterByMonth(allRecords, year, month);
        return buildDREData(filtered, categories);
    }, [allRecords, categories, year, month]);

    return {
        dreData,
        categories,
        year,
        month,
        setYear,
        setMonth,
        loading,
        error,
        actions: {
            refresh: loadRecords,
        },
    };
};
